import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {ActivatedRoute, ParamMap, Router} from '@angular/router';
import {empty} from '../../functions';
import {StudentService} from '../../s../../shared/services/student.service';
import {catchError, map} from 'rxjs/operators';
import {of} from 'rxjs';
import {LocaleService} from '../../shared/services/locale.service';
import {faMapMarker, faAddressCard, faDownload, faLanguage, faBullhorn} from '@fortawesome/free-solid-svg-icons';
import {CallForOpportunityService} from '../../api/services/call-for-opportunity.service';
import {CallForOpportunity} from '../../api/models/call-for-opportunity';
import {sortAnnouncementsNewestFirst} from '../../shared/utils/call-for-opportunity.utils';
import {ModalService} from '../../shared/services/modal.service';
import {
  ModalCompanyStudentContactComponent
} from './modal-company-student-contact/modal-company-student-contact.component';

import {studentprofilePlaceholder} from '../../model/model.studentprofile';
import { StudentProfile, StudentProfileType } from 'src/app/api/models';
import {
  ProfileTypeUtils,
  ProfileLookingForUtils,
} from '../../shared/utils/student-profile.utils';

@Component({
    selector: 'app-company-student-profile',
    templateUrl: './company-student-profile.component.html',
    styleUrls: ['./company-student-profile.component.scss'],
    standalone: false
})
export class CompanyStudentProfileComponent implements OnInit {

  private readonly destroyRef = inject(DestroyRef);
  private readonly cfoService = inject(CallForOpportunityService);

  readonly isLoading = signal(false);

  /**
   * This student's announcements ("oproepen"). `null` while loading; the
   * template hides the whole section until there is at least one item.
   */
  readonly oproepen = signal<CallForOpportunity[] | null>(null);

  avatarPlaceholder = studentprofilePlaceholder.avatarPlaceholder;

  studentprofileTypes = StudentProfileType;

  // Static utils exposed for template access
  readonly ProfileTypeUtils = ProfileTypeUtils;
  readonly ProfileLookingForUtils = ProfileLookingForUtils;

  faMapMarker = faMapMarker;
  faAddressCard = faAddressCard;
  faDownload = faDownload;
  faLanguage = faLanguage;
  faBullhorn = faBullhorn;

  entity: StudentProfile = {
    id: '',
    firstName: '',
    infix:'',
    lastName: '',
    birthDate: '',
    phoneNumber: '',
    profileType: StudentProfileType.Pupil,
    avatarUri: '',
    profileStrength: 0,
    homeAddress: {
      street: '',
      number: '',
      numberSuffix: '',
      city: '',
      postalCode: '',
      country: '',
    },
    allowEmailNotifications: false,
    hasParentalConsent: true,
    isSubscribedToNewsletter: false,
    hasMediaConsent: false,
    hasAcceptedGdprPolicy: false,
    hasDataUsageConsent: false,
    parentEmailAddress: '',
    interests: [],
    areaOfInterests: <any> [],
    workExperiences: <any> [],
    educations: <any> [],
    languageProficiencies: <any> [],
    foreignExperiences: <any> [],
    studentProfileCvs: <any> []
  }

  // TrackBy functions for ngFor performance optimization
  trackById = (_index: number, item: { id?: string | null }): string =>
    item.id ?? `index-${_index}`;
  trackByIndex = (index: number): number => index;

  constructor(private router: Router, private route: ActivatedRoute, public studentService: StudentService, public locale: LocaleService, public modal: ModalService) { }

  ngOnInit(): void {

    this.route.paramMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params: ParamMap) => {
        const id: string = <string>params.get('id');
        // eslint-disable-next-line no-console
        console.log('[CompanyStudentProfile] paramMap emit, id =', id);
        if (!empty(id)) {
          this.loadProfileData(id);
        }
      });

  }

  /**
   * Load student profile data
   *
   * @param id
   */
  loadProfileData(id: string) {

    // eslint-disable-next-line no-console
    console.log('[CompanyStudentProfile] loadProfileData start, id =', id);
    this.isLoading.set(true);
    this.loadAnnouncements(id);

    this.studentService.apiGetStudentprofile(id)
      .pipe(
        catchError(this.studentService.handleError('apiGetStudentprofile', false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((result: any) => {
        // eslint-disable-next-line no-console
        console.log('[CompanyStudentProfile] response received, result =', result);
        this.isLoading.set(false);

        if (result !== false) {
          this.entity = Object.assign({}, result);
        }
      });

  }

  /**
   * Load this student's announcements ("oproepen") via the shared endpoint,
   * filtered server-side by StudentProfileId. The response is a paged wrapper
   * ({ data, totalItems, ... }); we take its rows and show them newest first.
   */
  loadAnnouncements(studentProfileId: string) {
    this.oproepen.set(null);

    this.cfoService
      .getCallForOpportunities({ StudentProfileId: studentProfileId })
      .pipe(
        map((res) => (res.data ?? []) as CallForOpportunity[]),
        catchError(() => of<CallForOpportunity[]>([])),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((items) => {
        this.oproepen.set(sortAnnouncementsNewestFirst(items));
      });
  }

  /**
   * Treats null/empty and .NET `DateTime.MinValue` ("0001-01-01T00:00:00") as
   * "no end date" — both occur in the API payload.
   */
  isOpenEnded(dateString: string | null | undefined): boolean {
    return !dateString || dateString.startsWith('0001-01-01');
  }

  /**
   * Show contact details of current student profile entity
   */
  showContactDetails() {

    const data = {
      entity: this.entity
    }

    this.modal.Modal(ModalCompanyStudentContactComponent, '', '', data).then( (result) => {

    }).catch();

  }

  // TODO: probably is a feature in future
  hasDownloadPDF() {
    return false;
  }

  downloadPDF(): string|null {

    if (this.entity.studentProfileCvs && this.entity.studentProfileCvs.length > 0) {
      return this.entity.studentProfileCvs[0].uri || null;
    }
    return null;
  }

  goBack(): void
  {
    window.history.back();
  }

}
